import React, { useMemo } from "react";
import type { Theme } from "../lib/theme";
import type { FinalTotals } from "../lib/calculations";
import type { AltRollup, AltStatus } from "../lib/alternates";
import { summarizeAlternates } from "../lib/alternates";
import AlternatePackageList from "../components/AlternatePackageList";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface AlternatesTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  /** Alternates already gathered into packages. */
  rollups: AltRollup[];
  final: FinalTotals;
  fmt: (n: number) => string;
  setActiveTab: (tab: string) => void;
  setVisibleCols: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  navigateToItem: (itemId: string) => void;
  onStatus: (pkgId: string, status: AltStatus) => void;
  onRename: (pkgId: string, name: string) => void;
  onCombine: (pkgIds: string[], name: string) => void;
  onSplit: (pkgId: string) => void;
}

export default function AlternatesTab({
  T, isMobile, isTablet, rollups, final, fmt,
  setActiveTab, setVisibleCols, navigateToItem,
  onStatus, onRename, onCombine, onSplit,
}: AlternatesTabProps) {
  const s = useMemo(() => summarizeAlternates(rollups), [rollups]);

  // ── What the anchor actually is ────────────────────────────────────────────
  //
  // final.total is the project total as it stands, and an accepted alternate
  // is already inside it -- accepting one moves the figure in the header.
  //
  // This tab used to call that number "Base Bid (excl. alternates)" and then
  // print "Bid with accepted: base + accepted", adding the accepted scope a
  // second time. On the sandbox project that read $8,404,658 for a bid that
  // was $8,319,658: a confident, wrong number, with a label contradicting it.
  // Both are gone. The anchor is the current bid, and only what is still
  // undecided moves away from it.
  const currentBid = final.total;
  const acceptedNet = s.acceptedNet;
  const upside = s.maxUp;      // pending adds only
  const downside = s.maxDown;  // pending deducts only
  const minBid = currentBid - downside;
  const maxBid = currentBid + upside;

  // ── Bid Range Bar ──────────────────────────────────────────────
  // Where the bid sits now, and how far the undecided alternates could still
  // carry it. Accepted alternates are not marked separately any more: they are
  // inside the anchor, so a second mark for them was the double-count drawn.
  const BidRangeBar = () => {
    const range = maxBid - minBid || 1;
    const hasUp = upside > 0, hasDown = downside > 0;
    const W = 540, H = 130;
    const barLeft = 24, barRight = W - 24;
    const barW = barRight - barLeft, barY = 48, barH = 22;
    const xFor = (v: number) => barLeft + ((v - minBid) / range) * barW;
    const xNow = xFor(currentBid);
    const nowAnchor = !hasDown ? "start" : !hasUp ? "end" : "middle";
    const above = barY - 13, below = barY + barH + 18, deltaRow = barY + barH + 34;

    return (
      <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "20px 24px", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: ".12em", textTransform: "uppercase" }}>Where the bid could land</div>
          <div style={{ fontSize: 10, color: T.textWeak }}>
            Undecided swing: <span style={{ color: T.textSecondary, fontWeight: 600 }}>{fmt(upside + downside)}</span>
          </div>
        </div>
        {/* The cap sits on the drawing, not on the panel. The panel runs the
            width of the page so its border lines up with the cards above it;
            the chart stops at 820 so its labels stay near their intended
            size instead of scaling into headings. */}
        <div style={{ maxWidth: 820 }}>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible", display: "block" }}>
          <rect x={barLeft} y={barY} width={barW} height={barH} fill={T.borderPrimary} rx={4} opacity={0.35} />
          {hasDown && <rect x={barLeft} y={barY} width={Math.max(0, xNow - barLeft)} height={barH} fill={T.altStyle.Ded.leftBar} rx={4} opacity={0.18} />}
          {hasUp && <rect x={xNow} y={barY} width={Math.max(0, barRight - xNow)} height={barH} fill={T.altStyle.Alt.leftBar} rx={4} opacity={0.18} />}
          {hasDown && <line x1={barLeft} y1={barY - 4} x2={barLeft} y2={barY + barH + 4} stroke={T.textWeak} strokeWidth={1.5} opacity={0.55} />}
          {hasUp && <line x1={barRight} y1={barY - 4} x2={barRight} y2={barY + barH + 4} stroke={T.textWeak} strokeWidth={1.5} opacity={0.55} />}
          <line x1={xNow} y1={barY - 7} x2={xNow} y2={barY + barH + 7} stroke={T.borderAccent} strokeWidth={2.5} />
          {hasDown && <text x={barLeft} y={above} textAnchor="start" fill={T.textMuted} fontSize={10} fontFamily={FONT}>{fmt(minBid)}</text>}
          {hasUp && <text x={barRight} y={above} textAnchor="end" fill={T.textMuted} fontSize={10} fontFamily={FONT}>{fmt(maxBid)}</text>}
          <text x={xNow} y={above} textAnchor={nowAnchor} fill={T.borderAccent} fontSize={10} fontFamily={FONT} fontWeight={700}>{fmt(currentBid)}</text>
          {hasDown && <text x={barLeft} y={below} textAnchor="start" fill={T.textWeak} fontSize={10} fontFamily={FONT}>All deducts taken</text>}
          {hasUp && <text x={barRight} y={below} textAnchor="end" fill={T.textWeak} fontSize={10} fontFamily={FONT}>All adds taken</text>}
          <text x={xNow} y={below} textAnchor={nowAnchor} fill={T.textMuted} fontSize={10} fontFamily={FONT}>Bid today</text>
          {hasDown && <text x={barLeft} y={deltaRow} textAnchor="start" fill={T.altStyle.Ded.badgeText} fontSize={10} fontFamily={FONT} fontWeight={600}>&#8722;{fmt(downside)}</text>}
          {hasUp && <text x={barRight} y={deltaRow} textAnchor="end" fill={T.altStyle.Alt.badgeText} fontSize={10} fontFamily={FONT} fontWeight={600}>+{fmt(upside)}</text>}
        </svg>
        </div>
        {/* Said plainly, because the gap is real and a reader would otherwise
            assume these are dollars to the owner. */}
        <div style={{ fontSize: 10, color: T.textWeak, marginTop: 14, lineHeight: 1.6, maxWidth: 760 }}>
          Alternate figures are line-item scope values. Overhead, profit and contingency are applied when an
          alternate is accepted, so accepting one moves the bid by more than the figure shown here.
        </div>
      </div>
    );
  };

  /**
   * A figure and what it means. The old cards carried a line of small print
   * under the money that read "3 items", which is the one fact about an
   * alternate that does not matter: a bathroom described across six lines is
   * still one question, and six lines of tile trim is still one question.
   * What belongs there is the part of the money the reader cannot see.
   */
  const Stat = ({ label, value, color, note }: { label: string; value: string; color: string; note?: React.ReactNode }) => (
    <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6,
      padding: "14px 18px", flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {note && <div style={{ fontSize: 10, color: T.textMuted, marginTop: 5 }}>{note}</div>}
    </div>
  );

  return (
    <div className="fade-in" style={{ padding: isMobile ? "14px 16px" : isTablet ? "20px 24px" : "28px 36px", maxWidth: 1400 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: T.borderAccent, marginBottom: 4 }}>Alternates</div>
      <div style={{ fontSize: 11, color: T.textWeak, marginBottom: 22, maxWidth: 760, lineHeight: 1.6 }}>
        One row per decision the owner is being asked to make. Select two or more and combine them when several
        line items describe a single scope change — a bathroom priced across plumbing, drywall and MEPs is one
        alternate, not four. Accepting one puts its lines into the bid; rejecting one keeps them on the record
        and out of every total.
      </div>

      {rollups.length === 0 ? (
        <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "40px", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: T.textMuted, marginBottom: 8 }}>No alternates defined</div>
          <div style={{ fontSize: 11, color: T.textWeak, marginBottom: 16 }}>In the Estimate tab, turn on the "Alt Type" column via Columns menu, then set any line item to Alternate or Rejected.</div>
          <button className="add-row-btn" onClick={() => { setActiveTab("estimate"); setVisibleCols((p) => ({ ...p, altType: true })); }}>
            Go to Estimate → Enable Alt Type column
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
            <Stat label="Current Bid" value={fmt(currentBid)} color={T.borderAccent}
              note="All markups applied. Accepted alternates included." />
            <Stat label="Accepted" value={`${acceptedNet >= 0 ? "+" : "−"}${fmt(Math.abs(acceptedNet))}`}
              color={Math.abs(acceptedNet) < 0.5 ? T.textMuted : acceptedNet > 0 ? T.negative : T.positive}
              note={Math.abs(acceptedNet) < 0.5 ? "Nothing accepted yet." : "Scope value already inside the bid above"} />
            <Stat label="Still Open" value={`${s.pendingNet >= 0 ? "+" : "−"}${fmt(Math.abs(s.pendingNet))}`}
              color={s.pendingCount === 0 ? T.textMuted : T.costStatus["Needs Review"].badgeText}
              note={s.pendingCount === 0 ? "Every alternate is decided."
                : `${s.pendingCount} decision${s.pendingCount === 1 ? "" : "s"} outstanding, if all were taken`} />
            <Stat label="Swing" value={fmt(upside + downside)} color={T.textSecondary}
              note={<>{fmt(minBid)} &ndash; {fmt(maxBid)}</>} />
          </div>

          {(upside > 0 || downside > 0) && <BidRangeBar />}

          <AlternatePackageList
            rollups={rollups} T={T} fmt={fmt} navigateToItem={navigateToItem}
            onStatus={onStatus} onRename={onRename} onCombine={onCombine} onSplit={onSplit}
          />
        </>
      )}
    </div>
  );
}
